import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useTenant, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import AdRotator from '../components/AdRotator';
import SenArtisteLogo from '../components/SenArtisteLogo';
import { Sun, Moon, ArrowLeft, Calendar, MapPin, Ticket, Plus, Minus, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper pour résoudre les URLs d'images (relatives ou absolues)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

const EventDetail = () => {
  const { artistSlug, eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { tenant, fetchTenant } = useTenant();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [quantity, setQuantity] = useState(1);  // Rétrocompatibilité pour ancien système
  const [ticketSelections, setTicketSelections] = useState({});  // Nouveau: {ticketTypeId: quantity}
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const [purchasedTickets, setPurchasedTickets] = useState([]);
  
  // États pour le paiement DKR
  const [dkrPaymentDialog, setDkrPaymentDialog] = useState(false);
  const [dkrPaymentData, setDkrPaymentData] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  
  const [buyerForm, setBuyerForm] = useState({
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    paymentMethod: 'orange-money',
  });

  useEffect(() => {
    fetchTenant(artistSlug);
    fetchEvent();
  }, [artistSlug, eventId]);

  useEffect(() => {
    if (user) {
      setBuyerForm(prev => ({
        ...prev,
        buyerName: `${user.firstName} ${user.lastName}`,
        buyerEmail: user.email,
        buyerPhone: user.phone || '',
      }));
    }
  }, [user]);

  // Mettre à jour le mode de paiement selon la devise de l'événement
  useEffect(() => {
    if (event) {
      const defaultMethod = event.currency === 'EUR' ? 'card' : 'orange-money';
      setBuyerForm(prev => ({
        ...prev,
        paymentMethod: defaultMethod,
      }));
    }
  }, [event]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Événement non trouvé');
      navigate(`/${artistSlug}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price, currency = 'XOF') => {
    const currencyCode = currency === 'FCFA' ? 'XOF' : currency;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Helper pour vérifier si l'événement a des types de billets multiples
  const hasMultipleTicketTypes = event?.ticketTypes && event.ticketTypes.length > 0;
  const commissionPaidBy = event?.commissionPaidBy || 'artist';

  // Calcul du total pour les types de billets multiples
  const calculateTotal = () => {
    if (!event) return { baseTotal: 0, commission: 0, dkrFee: 0, total: 0, totalQuantity: 0, breakdown: [] };
    
    let baseTotal = 0;
    let totalQuantity = 0;
    const breakdown = [];
    
    if (hasMultipleTicketTypes) {
      event.ticketTypes.forEach(tt => {
        const qty = ticketSelections[tt.id] || 0;
        if (qty > 0) {
          const subtotal = tt.price * qty;
          baseTotal += subtotal;
          totalQuantity += qty;
          breakdown.push({
            name: tt.name,
            unitPrice: tt.price,
            quantity: qty,
            subtotal: subtotal
          });
        }
      });
    } else {
      // Ancien système à prix unique
      baseTotal = (event.ticketPrice || 0) * quantity;
      totalQuantity = quantity;
      breakdown.push({
        name: 'Standard',
        unitPrice: event.ticketPrice,
        quantity: quantity,
        subtotal: baseTotal
      });
    }
    
    // Calcul de la commission selon le choix de l'artiste
    let commission = 0;
    let total = baseTotal;
    
    if (commissionPaidBy === 'fan') {
      commission = baseTotal * 0.05;
      total = baseTotal + commission;  // Le fan paie 105%
    }
    
    // Frais de paiement Wave/Orange Money (1% du prix facial) - FCFA uniquement
    let dkrFee = 0;
    if (event.currency !== 'EUR') {
      dkrFee = Math.floor(baseTotal * 0.01);
      total = total + dkrFee;
    }
    
    return { baseTotal, commission, dkrFee, total, totalQuantity, breakdown };
  };

  const { baseTotal, commission, dkrFee, total: totalPrice, totalQuantity, breakdown } = calculateTotal();
  
  // Variables pour l'affichage EUR (frais de service)
  const ticketTotal = hasMultipleTicketTypes ? baseTotal : (event?.ticketPrice || 0) * quantity;
  const serviceFeePerTicket = 0.50; // 0,50€ par billet (fixe)
  const serviceFee = event?.currency === 'EUR' ? serviceFeePerTicket * totalQuantity : 0;
  const totalWithServiceFee = event?.currency === 'EUR' ? totalPrice + serviceFee : totalPrice;

  // Helper pour mettre à jour la quantité d'un type de billet
  const updateTicketSelection = (ticketTypeId, delta) => {
    setTicketSelections(prev => {
      const current = prev[ticketTypeId] || 0;
      const newValue = Math.max(0, current + delta);
      return { ...prev, [ticketTypeId]: newValue };
    });
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    
    if (!buyerForm.buyerName || !buyerForm.buyerEmail || !buyerForm.buyerPhone) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (totalQuantity <= 0) {
      toast.error('Veuillez sélectionner au moins un billet');
      return;
    }
    
    setPurchasing(true);
    
    try {
      // Préparer les sélections de billets
      const ticketSelectionsArray = hasMultipleTicketTypes 
        ? Object.entries(ticketSelections)
            .filter(([_, qty]) => qty > 0)
            .map(([ticketTypeId, qty]) => ({ ticketTypeId, quantity: qty }))
        : null;
      
      // Vérifier si c'est un paiement Stripe (EUR avec carte)
      if (event.currency === 'EUR' && buyerForm.paymentMethod === 'card') {
        // Créer une session Stripe Checkout
        const stripeResponse = await axios.post(`${API_URL}/stripe/create-checkout`, {
          event_id: eventId,
          quantity: hasMultipleTicketTypes ? totalQuantity : quantity,
          ticket_selections: ticketSelectionsArray,
          buyer_name: buyerForm.buyerName,
          buyer_email: buyerForm.buyerEmail,
          buyer_phone: buyerForm.buyerPhone,
          origin_url: window.location.origin,
        });
        
        if (stripeResponse.data.checkout_url) {
          // Rediriger vers Stripe Checkout
          toast.info('Redirection vers le paiement sécurisé...');
          window.location.href = stripeResponse.data.checkout_url;
          return;
        }
      }
      
      // Pour FCFA (Orange Money / Wave) - DKR Proxy
      if (event.currency === 'FCFA' || event.currency === 'XOF') {
        const dkrResponse = await axios.post(`${API_URL}/dkr/create-checkout`, {
          event_id: eventId,
          quantity: hasMultipleTicketTypes ? totalQuantity : quantity,
          ticket_selections: ticketSelectionsArray,
          buyer_name: buyerForm.buyerName,
          buyer_email: buyerForm.buyerEmail,
          buyer_phone: buyerForm.buyerPhone,
          payment_method: buyerForm.paymentMethod,
          origin_url: window.location.origin,
        });
        
        const data = dkrResponse.data;
        
        if (data.provider === 'wave' && data.wave_launch_url) {
          // Wave: ouvrir l'URL Wave directement
          toast.info('Redirection vers Wave...');
          window.location.href = data.wave_launch_url;
          return;
        }
        
        if (data.provider === 'orange') {
          // Orange Money: afficher le QR code dans un dialogue
          setDkrPaymentData(data);
          setDkrPaymentDialog(true);
          setPaymentDialogOpen(false);
          
          // Démarrer le polling du statut
          startPaymentPolling(data.payment_id);
          return;
        }
      }
      
      // Fallback - ne devrait pas arriver
      toast.error('Méthode de paiement non disponible');
    } catch (error) {
      // Gestion des erreurs DKR avec messages user-friendly
      const errorDetail = error.response?.data?.detail;
      let userMessage = 'Erreur lors de l\'achat';
      
      if (typeof errorDetail === 'string') {
        try {
          // Essayer de parser si c'est du JSON
          const parsed = JSON.parse(errorDetail);
          if (parsed.provider_error?.code === 'ip-not-allowed') {
            userMessage = 'Wave temporairement indisponible. Veuillez utiliser Orange Money.';
          } else if (parsed.provider_error?.message) {
            userMessage = parsed.provider_error.message;
          }
        } catch {
          // Si le parsing échoue, utiliser le detail directement
          userMessage = errorDetail;
        }
      } else if (errorDetail) {
        userMessage = errorDetail;
      }
      
      toast.error(userMessage);
    } finally {
      setPurchasing(false);
    }
  };

  // Polling du statut de paiement DKR
  const startPaymentPolling = (paymentId) => {
    setPollingStatus('pending');
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/dkr/status/${paymentId}`);
        const status = response.data.status;
        
        if (status === 'completed') {
          clearInterval(pollInterval);
          setPollingStatus('completed');
          setDkrPaymentDialog(false);
          toast.success('Paiement confirmé ! Vos billets sont prêts.');
          
          // Rediriger vers la page de succès
          window.location.href = `${window.location.origin}/checkout/success?payment_id=${paymentId}&provider=dkr`;
        } else if (status === 'failed' || status === 'cancelled') {
          clearInterval(pollInterval);
          setPollingStatus(status);
          setDkrPaymentDialog(false);
          toast.error('Le paiement a échoué ou a été annulé.');
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 4000); // Polling toutes les 4 secondes
    
    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (pollingStatus === 'pending') {
        setPollingStatus('timeout');
        toast.warning('Le délai de paiement a expiré. Veuillez réessayer.');
      }
    }, 300000);
  };

  // Ouvrir l'app Orange Money sur mobile
  const openOrangeMoneyApp = () => {
    if (dkrPaymentData?.deep_links?.OM) {
      window.location.href = dkrPaymentData.deep_links.OM;
    } else if (dkrPaymentData?.deep_link) {
      window.location.href = dkrPaymentData.deep_link;
    }
  };

  const downloadTicketPDF = async (ticket) => {
    const doc = new jsPDF();
    const artist = tenant?.artist;
    
    // Fonction pour charger une image en base64
    const loadImage = (url) => {
      return new Promise((resolve) => {
        if (!url) { resolve(null); return; }
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = () => resolve(null);
        img.src = url;
      });
    };

    // Charger les images de l'artiste
    const coverImage = await loadImage(resolveImageUrl(artist?.coverPhotoUrl));
    const profileImage = await loadImage(resolveImageUrl(artist?.profilePhotoUrl));
    
    // ===== EN-TÊTE AVEC IMAGE DE COUVERTURE =====
    if (coverImage) {
      try {
        doc.addImage(coverImage, 'JPEG', 0, 0, 210, 50);
        // Overlay semi-transparent
        doc.setFillColor(0, 0, 0);
        doc.setGState(new doc.GState({ opacity: 0.5 }));
        doc.rect(0, 0, 210, 50, 'F');
        doc.setGState(new doc.GState({ opacity: 1 }));
      } catch (e) {
        // Fallback couleur dorée
        doc.setFillColor(212, 175, 55);
        doc.rect(0, 0, 210, 50, 'F');
      }
    } else {
      doc.setFillColor(212, 175, 55);
      doc.rect(0, 0, 210, 50, 'F');
    }
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('SEN ARTISTE', 105, 22, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('BILLET D\'ENTRÉE OFFICIEL', 105, 38, { align: 'center' });
    
    // ===== INFORMATIONS ÉVÉNEMENT =====
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(event?.title || 'Événement', 105, 70, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`Artiste: ${artist?.nomDeScene || artistSlug}`, 20, 90);
    doc.text(`Date: ${formatDate(event?.dateTime)}`, 20, 100);
    doc.text(`Lieu: ${event?.location || 'À confirmer'}`, 20, 110);
    
    // ===== NUMÉRO DE BILLET =====
    doc.setFillColor(212, 175, 55);
    doc.rect(20, 125, 170, 30, 'F');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`N° ${ticket.ticketNumber}`, 105, 145, { align: 'center' });
    
    // ===== CATÉGORIE ET PRIX DU BILLET =====
    const ticketTypeName = ticket.ticketTypeName || 'Standard';
    const ticketPrice = ticket.paidPrice || ticket.ticketPrice || event?.ticketPrice || 0;
    const currency = event?.currency || 'FCFA';
    const priceDisplay = currency === 'EUR' 
      ? `${(ticketPrice / 100).toFixed(2)} €` 
      : `${ticketPrice.toLocaleString('fr-FR')} F CFA`;
    
    doc.setFillColor(245, 245, 245);
    doc.rect(20, 158, 170, 12, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(`${ticketTypeName}`, 25, 166);
    doc.setTextColor(212, 175, 55);
    doc.text(`${priceDisplay}`, 185, 166, { align: 'right' });
    
    // ===== INFORMATIONS ACHETEUR =====
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Acheteur: ${ticket.buyerName}`, 20, 185);
    doc.text(`Email: ${ticket.buyerEmail}`, 20, 195);
    
    // ===== QR CODE =====
    if (ticket.qrCodeData) {
      const qrImage = ticket.qrCodeData.startsWith('data:') 
        ? ticket.qrCodeData 
        : `data:image/png;base64,${ticket.qrCodeData}`;
      doc.addImage(qrImage, 'PNG', 70, 205, 70, 70);
    }
    
    // ===== PIED DE PAGE AVEC PHOTO ARTISTE =====
    doc.setFillColor(30, 30, 30);
    doc.rect(0, 275, 210, 22, 'F');
    
    // Photo de profil artiste en bas à gauche
    if (profileImage) {
      try {
        // Cercle pour la photo
        doc.addImage(profileImage, 'JPEG', 10, 277, 18, 18);
      } catch (e) {
        // Ignorer si l'image ne charge pas
      }
    }
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(`${artist?.nomDeScene || 'Artiste'} - senartiste.com`, 35, 288);
    
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 180);
    doc.text('Ce billet est personnel et non transférable. Présentez le QR code à l\'entrée.', 105, 294, { align: 'center' });
    
    doc.save(`billet-${ticket.ticketNumber}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <h1 className="text-2xl font-bold">{t('language') === 'fr' ? 'Événement non trouvé' : 'Event not found'}</h1>
        <Button onClick={() => navigate(`/${artistSlug}`)}>{t('language') === 'fr' ? 'Retour à la fanpage' : 'Back to fanpage'}</Button>
      </div>
    );
  }

  const artist = tenant?.artist;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-2 py-1 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(`/${artistSlug}`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('back')}
          </Button>
          
          <div className="flex items-center gap-2">
            <SenArtisteLogo className="w-6 h-6" />
            <span className="font-syne font-bold">{artist?.nomDeScene || artistSlug}</span>
          </div>
          
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
        {/* Event Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden"
        >
          <div 
            className="h-64 md:h-80"
            style={{
              backgroundImage: `url(${event.coverImageUrl || '/images/event-placeholder.jpg'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <span className="accent-text" style={{ color: artist?.primaryColor || '#D4AF37' }}>{t('event')}</span>
            <h1 className="font-syne text-3xl md:text-4xl font-extrabold mt-2">{event.title}</h1>
          </div>
        </motion.div>

        {/* Ad Banner */}
        <AdRotator tenantSlug={artistSlug} />

        <div className="grid md:grid-cols-3 gap-8">
          {/* Event Details */}
          <div className="md:col-span-2 space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Détails de l'événement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5" style={{ color: artist?.primaryColor || '#D4AF37' }} />
                  <div>
                    <p className="font-medium">Date et heure</p>
                    <p className="text-muted-foreground">{formatDate(event.dateTime)}</p>
                  </div>
                </div>
                
                {event.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5" style={{ color: artist?.primaryColor || '#D4AF37' }} />
                    <div>
                      <p className="font-medium">Lieu</p>
                      <p className="text-muted-foreground">{event.location}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <Ticket className="w-5 h-5 mt-1" style={{ color: artist?.primaryColor || '#D4AF37' }} />
                  <div className="flex-1">
                    <p className="font-medium mb-2">Prix des billets</p>
                    {hasMultipleTicketTypes ? (
                      <div className="space-y-2">
                        {event.ticketTypes.map(tt => (
                          <div key={tt.id} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                            <span className="font-medium">{tt.name}</span>
                            <span className="font-bold" style={{ color: artist?.primaryColor || '#D4AF37' }}>
                              {formatPrice(tt.price, event.currency)}
                            </span>
                          </div>
                        ))}
                        {commissionPaidBy === 'fan' && (
                          <p className="text-xs text-muted-foreground mt-2">
                            * Frais de service de 5% ajoutés au moment du paiement
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-2xl font-bold" style={{ color: artist?.primaryColor || '#D4AF37' }}>
                        {formatPrice(event.ticketPrice, event.currency)}
                      </p>
                    )}
                  </div>
                </div>
                
                {event.description && (
                  <div className="pt-4 border-t border-border">
                    <p className="font-medium mb-2">Description</p>
                    <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Purchase Card */}
          <div>
            <Card className="glass-card sticky top-24">
              <CardHeader>
                <CardTitle>Acheter des billets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sélection de billets */}
                {hasMultipleTicketTypes ? (
                  // Nouveau système: plusieurs types de billets
                  <div className="space-y-3">
                    {event.ticketTypes.map(tt => (
                      <div key={tt.id} className="p-3 border border-border rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <p className="font-medium">{tt.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatPrice(tt.price, event.currency)}
                              {commissionPaidBy === 'fan' && <span className="text-xs"> + 5%</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateTicketSelection(tt.id, -1)}
                              disabled={(ticketSelections[tt.id] || 0) <= 0}
                              data-testid={`quantity-minus-${tt.id}`}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center font-bold">{ticketSelections[tt.id] || 0}</span>
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateTicketSelection(tt.id, 1)}
                              data-testid={`quantity-plus-${tt.id}`}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {tt.quantity && (
                          <p className="text-xs text-muted-foreground">
                            {tt.quantity - (tt.sold || 0)} disponible(s)
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Ancien système: un seul type de billet
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Quantité</span>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                        data-testid="quantity-minus"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-bold">{quantity}</span>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setQuantity(q => q + 1)}
                        data-testid="quantity-plus"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="border-t border-border pt-4">
                  {/* Détail du prix */}
                  {totalQuantity > 0 && (
                    <div className="space-y-2 mb-4 text-sm">
                      {breakdown.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-muted-foreground">
                          <span>{item.quantity}x {item.name}</span>
                          <span>{formatPrice(item.subtotal, event.currency)}</span>
                        </div>
                      ))}
                      {commissionPaidBy === 'fan' && commission > 0 && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Frais de service (5%)</span>
                          <span>{formatPrice(commission, event.currency)}</span>
                        </div>
                      )}
                      {event?.currency !== 'EUR' && dkrFee > 0 && (
                        <div className="flex justify-between text-muted-foreground" data-testid="dkr-fee-line">
                          <span>Frais de paiement Wave/OM (1%)</span>
                          <span>{formatPrice(dkrFee, event.currency)}</span>
                        </div>
                      )}
                      {event?.currency === 'EUR' && serviceFee > 0 && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Frais de paiement</span>
                          <span>{formatPrice(serviceFee * 100, 'EUR')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium">Total</span>
                    <span className="text-2xl font-bold" style={{ color: artist?.primaryColor || '#D4AF37' }}>
                      {formatPrice(event?.currency === 'EUR' ? totalWithServiceFee * 100 : totalPrice, event?.currency)}
                    </span>
                  </div>
                  
                  <Button 
                    className="w-full glow-gold"
                    style={{ backgroundColor: artist?.primaryColor || '#D4AF37' }}
                    onClick={() => setPaymentDialogOpen(true)}
                    disabled={totalQuantity <= 0}
                    data-testid="buy-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {totalQuantity <= 0 ? 'Sélectionnez des billets' : 'Acheter maintenant'}
                  </Button>
                </div>
                
                <p className="text-xs text-center text-muted-foreground">
                  {event?.currency === 'EUR' 
                    ? 'Paiement sécurisé par Stripe'
                    : 'Paiement sécurisé (Wave / Orange Money)'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Finaliser l'achat</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePurchase} className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Événement</p>
              <p className="font-bold">{event.title}</p>
              <div className="mt-3 space-y-1 text-sm">
                {/* Affichage détaillé des billets sélectionnés */}
                {breakdown.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-muted-foreground">
                    <span>{item.quantity}x {item.name} à {formatPrice(item.unitPrice, event?.currency)}</span>
                    <span>{formatPrice(item.subtotal, event?.currency)}</span>
                  </div>
                ))}
                {commissionPaidBy === 'fan' && commission > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Frais de service (5%)</span>
                    <span>{formatPrice(commission, event?.currency)}</span>
                  </div>
                )}
                {event?.currency !== 'EUR' && dkrFee > 0 && (
                  <div className="flex justify-between text-muted-foreground" data-testid="dkr-fee-line-dialog">
                    <span>Frais de paiement Wave/OM (1%)</span>
                    <span>{formatPrice(dkrFee, event?.currency)}</span>
                  </div>
                )}
                {event?.currency === 'EUR' && serviceFee > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Frais de paiement ({totalQuantity} × 0,50€)</span>
                    <span>{formatPrice(serviceFee * 100, 'EUR')}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border font-bold text-foreground">
                  <span>Total à payer</span>
                  <span>{formatPrice(event?.currency === 'EUR' ? totalWithServiceFee * 100 : totalPrice, event?.currency)}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nom complet</Label>
              <Input 
                value={buyerForm.buyerName}
                onChange={(e) => setBuyerForm(p => ({ ...p, buyerName: e.target.value }))}
                required
                data-testid="buyer-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={buyerForm.buyerEmail}
                onChange={(e) => setBuyerForm(p => ({ ...p, buyerEmail: e.target.value }))}
                required
                data-testid="buyer-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input 
                value={buyerForm.buyerPhone}
                onChange={(e) => setBuyerForm(p => ({ ...p, buyerPhone: e.target.value }))}
                placeholder="+221 77 xxx xx xx"
                required
                data-testid="buyer-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Mode de paiement</Label>
              <Select 
                value={buyerForm.paymentMethod} 
                onValueChange={(v) => setBuyerForm(p => ({ ...p, paymentMethod: v }))}
              >
                <SelectTrigger data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Afficher uniquement les moyens de paiement selon la devise */}
                  {event?.currency === 'EUR' ? (
                    <SelectItem value="card">Carte Bancaire (SEPA)</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="orange-money">Orange Money</SelectItem>
                      <SelectItem value="wave">Wave</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {event?.currency === 'EUR' 
                  ? 'Paiement par carte bancaire via Stripe'
                  : 'Paiement mobile money (Wave / Orange Money)'}
              </p>
            </div>
            
            <DialogFooter>
              <Button 
                type="submit" 
                className="w-full"
                style={{ backgroundColor: artist?.primaryColor || '#D4AF37' }}
                disabled={purchasing}
                data-testid="confirm-purchase"
              >
                {purchasing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payer {formatPrice(event?.currency === 'EUR' ? totalWithServiceFee * 100 : totalPrice, event?.currency)}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tickets Dialog */}
      <Dialog open={ticketsDialogOpen} onOpenChange={setTicketsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vos billets 🎉</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Félicitations! Voici vos billets. Téléchargez-les en PDF et présentez le QR code à l'entrée.
            </p>
            
            {purchasedTickets.map((ticket) => (
              <Card key={ticket.id} className="glass-card" data-testid={`purchased-ticket-${ticket.id}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-mono text-lg font-bold">{ticket.ticketNumber}</p>
                      {ticket.ticketTypeName && ticket.ticketTypeName !== 'Standard' && (
                        <p className="text-sm font-medium" style={{ color: artist?.primaryColor || '#D4AF37' }}>
                          {ticket.ticketTypeName}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">{ticket.buyerName}</p>
                      <p className="text-xs text-muted-foreground">{ticket.buyerEmail}</p>
                    </div>
                    {ticket.qrCodeData && (
                      <img 
                        src={`data:image/png;base64,${ticket.qrCodeData}`} 
                        alt="QR Code" 
                        className="w-20 h-20"
                      />
                    )}
                  </div>
                  <Button 
                    className="w-full mt-4"
                    variant="outline"
                    onClick={() => downloadTicketPDF(ticket)}
                    data-testid={`download-ticket-${ticket.id}`}
                  >
                    Télécharger PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => { setTicketsDialogOpen(false); navigate(`/${artistSlug}`); }}>
              Retour à la fanpage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orange Money QR Code Dialog */}
      <Dialog open={dkrPaymentDialog} onOpenChange={(open) => {
        if (!open && pollingStatus === 'pending') {
          // L'utilisateur ferme le dialogue pendant le paiement
          toast.warning('Le paiement est toujours en cours. Vous serez notifié une fois confirmé.');
        }
        setDkrPaymentDialog(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Paiement Orange Money</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* QR Code */}
            {dkrPaymentData?.qr_code && (
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-xl shadow-lg">
                  <img 
                    src={`data:image/png;base64,${dkrPaymentData.qr_code}`}
                    alt="QR Code Orange Money"
                    className="w-48 h-48"
                    data-testid="orange-qr-code"
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  Scannez ce QR code avec l'application Orange Money
                </p>
              </div>
            )}
            
            {/* Montant */}
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: artist?.primaryColor || '#FF6600' }}>
                {formatPrice(dkrPaymentData?.amount || 0, 'FCFA')}
              </p>
            </div>
            
            {/* Bouton pour ouvrir l'app */}
            {(dkrPaymentData?.deep_links?.OM || dkrPaymentData?.deep_link) && (
              <Button 
                onClick={openOrangeMoneyApp}
                className="w-full"
                style={{ backgroundColor: '#FF6600' }}
                data-testid="open-orange-app"
              >
                Ouvrir l'application Orange Money
              </Button>
            )}
            
            {/* Statut du polling */}
            <div className="flex items-center justify-center gap-2 text-sm">
              {pollingStatus === 'pending' && (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                  <span className="text-muted-foreground">En attente de confirmation...</span>
                </>
              )}
              {pollingStatus === 'completed' && (
                <span className="text-green-600 font-medium">Paiement confirmé !</span>
              )}
              {pollingStatus === 'failed' && (
                <span className="text-red-600 font-medium">Paiement échoué</span>
              )}
              {pollingStatus === 'timeout' && (
                <span className="text-yellow-600 font-medium">Délai expiré - Veuillez réessayer</span>
              )}
            </div>
            
            {/* Instructions */}
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 text-sm">
              <p className="font-medium text-orange-800 dark:text-orange-200 mb-2">Instructions :</p>
              <ol className="list-decimal list-inside text-orange-700 dark:text-orange-300 space-y-1">
                <li>Ouvrez l'application Orange Money</li>
                <li>Scannez le QR code ou cliquez sur le bouton ci-dessus</li>
                <li>Confirmez le paiement avec votre code secret</li>
                <li>Attendez la confirmation (vérification automatique)</li>
              </ol>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDkrPaymentDialog(false)}
              className="w-full"
            >
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail;
